/* eslint-disable class-methods-use-this */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component, Inject, Input, OnDestroy, OnInit,
} from '@angular/core';

// eslint-disable-next-line import/no-extraneous-dependencies
import { ClassicEditor, BalloonEditor, type EditorConfig } from 'ckeditor5';
import { ComponentInjectorService } from 'src/common/component-injector.service';
import { UploadAssetService } from 'src/common/upload-asset.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { BulbCKEStateService } from '../services/bulb-cke-state.service';
// import CKEDITOR from '../../dist/ckeditor';

import { ErrorService } from '../../../common/error.service';
import { AppConfigService } from '../../../common/services/app-config/app-config.service';
import { MEDIA, UndoRedoService } from '../services/undo-redo.service';
import { CKEditorService } from '../services/ckeditor.service';
import { CKEEventService } from '../services/cke-event.service';
import { ImageCarouselService } from '../services/image-carousel.service';
import CKEditorUtils from '../../utils';
import editorConfig from '../../editorconfig.constant';
import { COMMENTS_CONFIGURE, MEDIA_CONFIG } from '../../editorcustomconfig.constant';
import { EditorUtil } from '../editor.util';

let bulbAutoSaveService;
let bulbPageService : any = null;
/**
 * Component for page CKEditor instance.
 */
@Component({
  selector: 'bulb-page-editor',
  templateUrl: './page-editor.component.html',
  styleUrls: ['./page-editor.component.scss'],
  standalone: false,
})
export class PageEditorComponent implements OnInit, OnDestroy {
  private readonly AUTOSAVE_EVENT = 'bulbAutoSave';// AJS constant

  private readonly BLOCK_ELEMENT_UNLOAD = 'blockElementUnload'; // constant for deleting block

  /**
   * Debug variable. Used for inspecting the cke editor instance.
   */
  private inspectEditor: boolean = this.appConfigService.editor.inspect;

  /**
   * If the page is editable.
   */
  @Input() editable: boolean;

  /**
   * If the user is signed in.
   */
  @Input() authenticated: boolean;

  /**
   * Selected editor language.
   */
  @Input() language: string;

  /**
   * Page input property.
   */
  @Input() page: any;

  /**
   * Determines if the associated feature or component is allowed for the user.
   */
  @Input() allowed: boolean;

  /**
   * Store last updated content.
   * @private
   */
  private lastPageUnitContent : string;

  /**
   * Editor instance.
   */
  public EditorBuild : any = null;

  /**
   * CKEditor instance.
   */
  editor: ClassicEditor | BalloonEditor;

  /**
    * subscriber for BehaviorSubject Observable.
    */
  subscription: Subscription;

  /**
   * Decides the type of editor to show classic or balloon.
   */
  showClassicEditor: boolean = true;

  /**
   * flag for showing/hiding character counter in editor.
   */
  showWordCount = false;

  /**
   * Current word count.
   */
  wordCount: number = 0;

  /**
   * Lock id
   */
  featureLockId = Symbol('viewMode');

  /**
   * Default Config object for our ckeditor instance.
   */
  public defaultConfig : EditorConfig = {
    placeholder: '',
    extraPlugins: [],
    language: 'en',
    ui: {
      viewportOffset: { top: 57 },
    },
  };

  /**
   * Default Constructor.
   *
   * @param BulbAutoSaveService Our BulbAutoSaveService AngularJS service.
   * @param BulbPageService Our BulbPageService AngularJS service.
   * @param appConfigService configuration service for application.
   * @param ckeditorService Service which have all service getting with cke plugin.
   * @param ckeEventService Service which have all events register with cke plugin.
   * @param componentInjectorService Injector service for dynamically load component.
   * @param errorService Our custom error service.
   * @param uploadAssetService Service to handle uploads.
   * @param undoRedoService Service which handle api communication for page editor.
   * @param bulbCKEStateService Service which holds CKE state/data.
   * @param imageCarouselService Service which handle image carousel operations.
   */
  constructor(
    @Inject('BulbAutoSaveService') private BulbAutoSaveService: any,
    @Inject('BulbPageService') private BulbPageService: any,
    private appConfigService: AppConfigService,
    private ckeditorService: CKEditorService,
    private ckeEventService: CKEEventService,
    private componentInjectorService : ComponentInjectorService,
    private errorService: ErrorService,
    private uploadAssetService : UploadAssetService,
    private bulbCKEStateService: BulbCKEStateService,
    private undoRedoService: UndoRedoService,
    private imageCarouselService: ImageCarouselService,
  ) {
    bulbAutoSaveService = this.BulbAutoSaveService;
    bulbPageService = this.BulbPageService;
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    this.setupEditor();
    this.defaultConfig.language = this.language;
    EditorUtil.addTranslations(this.ckeditorService.translate, this.defaultConfig);

    if (this.editable) {
      EditorUtil.updateColorLabels(this.defaultConfig.fontColor.colors, this.defaultConfig.language);
    }

    // Attach any of our custom bulb services onto our classicEditorBuild prototype
    // To use in any of our cke plugins here:
    this.EditorBuild.prototype.bulb = {
      page: this.BulbPageService.page,
      CONFIG: this.appConfigService.config,
      readOnly: !this.editable,
      service: this.ckeditorService,
    };

    globalThis.CKEBulbData = this.EditorBuild.prototype.bulb;

    this.defaultConfig.autosave = this.configureAutoSave();

    this.addUnloadEventListener();
  }

  /**
   * Method to setup editor.
   */
  setupEditor() {
    if (!this.editable) {
      this.showClassicEditor = false;
      // Calling BalloonEditor here
      this.EditorBuild = BalloonEditor;
      Object.assign(this.defaultConfig, editorConfig.balloonConfig);
      this.handleBalloonComment();
    } else {
      this.EditorBuild = ClassicEditor;
      Object.assign(this.defaultConfig, editorConfig.classicConfig);
    }
  }

  /**
   * Fired when the editor is ready. It corresponds with the editor#ready event.
   * It is fired with the editor instance.
   *
   * Upon the initialization of the editor, we handle a few key aspects:
   * - Set up the `autosave` logic
   * - Handle setting the correct data on the editor.
   *
   * @param editor Editor instance.
   */
  public onReady(editor) {
    this.editor = editor;
    this.ckeEventService.registerEvents(editor, this.page.unit, this.editable);
    this.configureUndoRedo(editor);

    this.initEditor(editor);
  }

  /**
   * Remove listener on page destroy.
   */
  ngOnDestroy(): void {
    this.bulbCKEStateService.assetType.next('');
    window.removeEventListener(this.BLOCK_ELEMENT_UNLOAD, this.handleDeletedBlock);
    window.removeEventListener('resize', () => EditorUtil.adjustWidthForSmallDevices(this.editor));
    window.removeEventListener('load', () => EditorUtil.adjustWidthForSmallDevices(this.editor));
    this.componentInjectorService.removeAllDynamicComponents();
    this.componentInjectorService.disconnectMutationObserver();
    document.removeEventListener('mouseup', EditorUtil.handleClickOutsideEditor);
    document.removeEventListener('mouseup', this.ckeditorService.addMarginToListForCKEditor);
    // Reset status false.
    this.bulbCKEStateService.isEventsRegisterForMarginToCkeLists = false;
    document.removeEventListener('mouseup', EditorUtil.handleClickOutsideEditor.bind(this.editor));
    // Remove Image Carousel Event Listeners from editor.
    this.removeImageCarouselEvents();

    if (this.editable) {
      this.subscription.unsubscribe();
    }
    globalThis.CKEBulbData = {
      page: {},
      CONFIG: {},
      readOnly: {},
      service: {},
    };
  }

  /**
   * Remove Image carousel event listeners.
   * @private
   */
  private removeImageCarouselEvents(): void {
    window.removeEventListener('addImageToCarousel', this.imageCarouselService.handleAddingSlideToCarousel);
    window.removeEventListener('imageCarouselAddSlide', this.imageCarouselService.handleImageCarouselSlideAdd);
    window.removeEventListener('removeImageFromCarousel', this.imageCarouselService.handleRemovingSlideFromCarousel);
    window.removeEventListener('changeCarouselCaption', this.imageCarouselService.updateCarouselCaption);
    window.removeEventListener('bulbCropCarouselImage', this.imageCarouselService.handleImageCarouselCrop);
    window.removeEventListener(
      'openAssetLibraryCarouselType',
      this.imageCarouselService.handleImageCarouselButtonClick,
    );
  }

  /**
   * Method that set withText true and label for table and divider plugin in media toolbar.
   *
   * @param editor CKE instance.
   */
  addLabelToToolbarButton(editor) {
    const mediaIndex = editor.config.get('toolbar').items.findIndex((mediaElement) => mediaElement === 'media');
    if (typeof mediaIndex === 'number') {
      const dividerIndex = MEDIA_CONFIG.options.findIndex((tableElement) => tableElement === 'horizontalLine');
      const tableIndex = MEDIA_CONFIG.options.findIndex((tableElement) => tableElement === 'insertTable');
      if (dividerIndex && tableIndex) {
        const mediaPlugin = editor.ui.view.toolbar.items.get(mediaIndex);
        mediaPlugin.buttonView.arrowView.element.style.display = 'none';
      }
    }
  }

  /**
   * Add the event listener for removing the image block element.
   */
  addUnloadEventListener() {
    window.addEventListener(this.BLOCK_ELEMENT_UNLOAD, this.handleDeletedBlock);
  }

  /**
   * To handle the deleted block for the image.
   * @param event
   */
  private handleDeletedBlock = (event) => {
    const { blockName } = event.detail;
    if (blockName === 'image') {
      this.componentInjectorService.removeDynamicComponent(event.detail.blockId);
    }
    if (MEDIA.includes(blockName)) {
      this.undoRedoService.handleUndoRedo(this.page.unit, event.detail.blockId, null, 'redo', 'remove');
    }
  };

  /**
   * Method to initialize the ckeditor.
   * Initialize the editor with the page content data.
   *
   * @param editor CKEditor instance.
   * @private
   */

  initEditor(editor) {
    // Create the pre-existing content on the editor.
    this.lastPageUnitContent = this.page.unit.content;
    // If we are debugging / inspecting the cke instance, lets attach the inspector to the editor instance:
    if (this.inspectEditor) {
      EditorUtil.attachEditor(editor);
    }

    // Enable read-only mode if the user is not logged in -- i.e Anonymous users:
    if (!this.editable) {
      EditorUtil.enableReadOnly(editor, this.page.unit.commentsEnabled);
    } else {
      // To handle show/hide wordcount on edit/view mode.
      this.showWordCount = true;
      const wordCountPlugin = editor.plugins.get('WordCount');
      this.wordCount = wordCountPlugin.words;
      wordCountPlugin.on('update', (evt, stats) => {
        this.wordCount = stats.words;
      });
      this.addLabelToToolbarButton(editor);
      this.getcommentEnabledSubject(editor);
    }

    if (!this.authenticated) {
      editor.enableReadOnlyMode(this.featureLockId);
    }
    // Add margin to list elements on CK editor page.
    this.ckeditorService.addMarginToListForCKEditor();

    EditorUtil.adjustWidthForSmallDevices(editor);
  }

  /**
   * Helper method that configures everything needed for our `autosave` logic.
   *
   * @private
   */
  private configureAutoSave(): any {
    return {
      waitingTime: 1500,
      save: (editor) => {
        // which should be resolved when the data is successfully saved.
        Promise.resolve()
          .then(() => {
            // Grab data from the editor, sanitize it, then send it off!
            const $data = $('<div />')
              .html(editor.getData());
            CKEditorUtils.handleSanitization($data);
            this.handleSaving($data, editor);
          });
      },
    };
  }

  /**
   * Conditionally save page content.
   *
   * @param $data Will be saved.
   * @param editor Current Editor.
   * @private
   */
  private handleSaving($data, editor) {
    if (!this.undoRedoService.undoRedoSubject.value) {
      this.save($data, editor);
    } else {
      this.undoRedoService.undoRedoSubject.subscribe(() => {
        if (!this.undoRedoService.undoRedoSubject.value) {
          this.save($data, editor);
          this.undoRedoService.undoRedoSubject.complete();
        }
      });
    }
  }

  /**
   * Save page content.
   *
   * @param $data Will be saved.
   * @param editor Current Editor.
   * @private
   */
  private save($data, editor) {
    if (this.bulbCKEStateService.handleSaveOperation.value) {
      bulbAutoSaveService.autoSave(
        this.page.unit.id,
        'units',
        { content: $data.html() },
        bulbPageService.updatePageAfterAutoSave,
      );
    }
    if (this.uploadAssetService.uploadSubject.value) {
      this.uploadAssetService.uploadSubject.next(false);
    }
    if (this.bulbCKEStateService.insertParagraphSubject.value) {
      EditorUtil.scrollAfterAssetInsertion(editor, this.bulbCKEStateService.insertParagraphAfterTableSubject.value);
    }
    this.lastPageUnitContent = editor.getData();
  }

  /**
   * Method that will send error to sentry if error occurs in autosave.
   *
   * @private
   */
  sendErrorToSentry(event) {
    if (event.detail && event.detail.status === 'AUTO_SAVE_ERROR') {
      this.errorService.handleException(`Error saving content on unit : ${this.page.unit.id} `
        + `with error => ${event.detail.error}`);
    }
  }

  /**
   * Helper method that configures everything needed for our `undo and redo` logic for blocks.
   *
   * @param editor CKE instance.
   * @private
   */
  private configureUndoRedo(editor): void {
    this.undoRedoService.configureChangeOnUndoRedo(editor, this.page.unit);
    const undoCommand = editor.commands.get('undo');
    undoCommand.on('execute', (eventInfo) => {
      this.handleUndoRedo(editor, eventInfo, 'undo');
    }, { priority: 'highest' });

    const redoCommand = editor.commands.get('redo');
    redoCommand.on('execute', (eventInfo) => {
      this.handleUndoRedo(editor, eventInfo, 'redo');
    }, { priority: 'highest' });
  }

  /**
   * Handle undo/redo action.
   *
   * @param editor Current Editor
   * @param eventInfo Event
   * @param action Undo/Redo
   */
  handleUndoRedo(editor, eventInfo, action) {
    const selectedElement = editor.model.document.selection.getSelectedElement();
    this.undoRedoService.configureUndoRedo(this.page.unit, selectedElement, eventInfo, action);
  }

  /**
   * Allow comment functionality only to authenticated user.
   */
  handleBalloonComment() {
    if (this.page?.unit.commentsEnabled && this.allowed) {
      Object.assign(this.defaultConfig, COMMENTS_CONFIGURE);
    }
  }

  /**
   * Handle subscription to isCommentEnabledSubject observable to change CommentToolbarStatus.
   */
  getcommentEnabledSubject(editor) {
    EditorUtil.setCommentToolbarStatus(editor, false);
    this.subscription = this.bulbCKEStateService.isCommentEnabledSubject.subscribe(() => {
      EditorUtil.setCommentToolbarVisibility(editor, this.BulbPageService.page.unit.commentsEnabled);
    });
  }
}
